import { useState } from 'react';
import SharedDialog from '../../../../shared/shared-dialog';
import GroupTemplateAutocomplete from '../../../../shared/group-template-autocomplete';
import {
  templateTableHeaderCellSx,
  templateTableRowCellSx,
  TEMPLATE_TABLE_MAX_HEIGHT,
} from '../../../../shared/template-table-styles';
import { useMutation } from '@tanstack/react-query';
import { bulkApplyGroupTemplate } from '../../../../api/users-api';
import { GroupTemplate } from '../../../../../types';
import { describeTemplateSettings } from '../../../../shared/group-settings.config';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { handleErrors } from '../../../../utils/errors';
import { Messages } from '../../../../utils/message-constants';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

interface BulkApplyTemplateDialogProps {
  open: boolean;
  handleClose: () => void;
  targetGroupIds: Record<string, string>;
  setSelectedRows: (rows: string[]) => void;
  directoryRoles: string[];
  changeLogRoles: string[];
}

export default function BulkApplyTemplateDialog({
  open,
  handleClose,
  targetGroupIds,
  setSelectedRows,
  directoryRoles,
  changeLogRoles,
}: BulkApplyTemplateDialogProps) {
  const theme = useTheme();
  const { setSnackbar } = useStore();
  const [selectedTemplate, setSelectedTemplate] =
    useState<GroupTemplate | null>(null);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const groupCount = Object.keys(targetGroupIds).length;

  const sections = selectedTemplate
    ? describeTemplateSettings(selectedTemplate)
    : null;

  const canApplyTemplate = isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.ApplyGroupTemplate.value
  );
  const requiresChangeLog = isAbleToSeeUIElement(
    changeLogRoles,
    DirectoryRoles.ApplyGroupTemplate.value
  );

  const { mutate: applyTemplate, isPending: applying } = useMutation({
    mutationFn: (logMessage: string) => {
      if (!selectedTemplate) return Promise.resolve();
      return bulkApplyGroupTemplate(
        targetGroupIds,
        selectedTemplate.id,
        logMessage
      );
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Applying template to ${groupCount} group${
          groupCount === 1 ? '' : 's'
        } — check back shortly.`,
      });
      setChangeLogMessage('');
      setOpenChangeLogDialog(false);
      setSelectedTemplate(null);
      setSelectedRows([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          'this group template'
        ),
      });
      setOpenChangeLogDialog(false);
    },
  });

  const handleApplyClick = () => {
    if (requiresChangeLog) {
      setOpenChangeLogDialog(true);
    } else {
      applyTemplate('');
    }
  };

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleClose}
        title="Apply Group Template"
        maxWidth="md"
        primaryButton={{
          action: handleApplyClick,
          title: `Apply Template to ${groupCount} Group${
            groupCount === 1 ? '' : 's'
          }`,
          disabled: !selectedTemplate || !canApplyTemplate || applying,
          loading: applying,
        }}
        dialogContent={
          <>
            <Typography
              variant="body2"
              sx={{ mb: 2, color: theme.palette.semantic.text.title2 }}
            >
              Choose a template to apply to {groupCount} selected group
              {groupCount === 1 ? '' : 's'}.
            </Typography>
            <GroupTemplateAutocomplete
              value={selectedTemplate}
              onChange={setSelectedTemplate}
            />

            {sections && (
              <>
                <Typography variant="body2" sx={{ mt: 3, mb: 1 }}>
                  These settings will be applied to every selected group. Since
                  selected groups may currently differ, no before/after
                  comparison is shown.
                </Typography>
                <Box
                  sx={{
                    maxHeight: TEMPLATE_TABLE_MAX_HEIGHT,
                    overflow: 'auto',
                    flexShrink: 0,
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {['Setting', 'Template Value'].map((heading) => (
                          <TableCell
                            key={heading}
                            sx={templateTableHeaderCellSx(theme)}
                          >
                            {heading}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sections
                        .flatMap((section) => section.rows)
                        .map((row) => {
                          const cellSx = templateTableRowCellSx(theme);
                          return (
                            <TableRow key={row.field}>
                              <TableCell sx={cellSx}>{row.label}</TableCell>
                              <TableCell sx={cellSx}>
                                {row.templateValue}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}
          </>
        }
      />

      {openChangeLogDialog && (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => applyTemplate(changeLogMessage)}
          mutateLoading={applying}
        />
      )}
    </>
  );
}
